import { useEffect, useState } from 'react'
import Checkbox from '@wiggui/ds/Checkbox'
import Flex from '@wiggui/ds/Flex'
import GoogleIcon from '@wiggui/ds/GoogleIcon'
import Select from '@wiggui/ds/Select'
import Text from '@wiggui/ds/Text'
import { useModalPublishPortalsErrorStore } from '../../../store/modals/modal-publish-portals-error.store'
import { useAdOptions } from './hooks/useAdOptions'
import { composeClasses } from '@wiggot/utils/src/strings'

interface AdOptionProps {
  img: string
  description?: string
  id: string
  isDisabledAndChecked?: boolean
  setDisabledConfirmButton?: (value: boolean) => void
  delayedText?: string
  publishIsFailed?: boolean
  options: any
}

export const AdOption = ({
  img,
  description,
  isDisabledAndChecked,
  id,
  setDisabledConfirmButton,
  delayedText,
  publishIsFailed,
  options
}: AdOptionProps) => {
  const selectedAdOptions = useModalPublishPortalsErrorStore(
    (state) => state?.selectedAdOptions
  )

  const adOptionSelected = selectedAdOptions?.find(
    (selectedAdOption) => selectedAdOption?.portalId === id
  )

  const {
    adOptions,
    isLoading: isLoadingAdOptions,
    isError: isErrorAdOptions
  } = useAdOptions({
    portalId: id
  })
  const toggleSelectedPortalId = useModalPublishPortalsErrorStore(
    (state) => state?.toggleSelectedPortalId
  )

  const handleToggle = () => {
    toggleSelectedPortalId(id)
  }

  const toggleSelectAdOption = useModalPublishPortalsErrorStore(
    (state) => state?.toggleSelectAdOption
  )

  const portalIsChecked = useModalPublishPortalsErrorStore(
    (state) => state?.portalIsChecked
  )

  const isChecked = portalIsChecked(id)

  const [openSelect, setOpenSelect] = useState(false)

  useEffect(() => {
    if (isChecked && adOptionSelected?.available === 0) {
      setDisabledConfirmButton?.(true)
      return
    }
    setDisabledConfirmButton?.(false)
  }, [adOptionSelected, setDisabledConfirmButton, isChecked])

  useEffect(() => {
    if (!isChecked && openSelect) {
      setOpenSelect(false)
    }
  }, [isChecked, openSelect])

  useEffect(() => {
    let hasChecked = false
    options.forEach((option: any) => {
      if (option.publishIsFailed && portalIsChecked(option.portalId)) {
        hasChecked = true
      }
    })
    if (!hasChecked) {
      setDisabledConfirmButton?.(true)
    }
  }, [isChecked])

  return (
    <Flex
      as="button"
      dir="col"
      items="start"
      onClick={() =>
        !isErrorAdOptions && !isDisabledAndChecked && handleToggle()
      }
      justify="between"
      gap="gap-4"
      wFull
      className={composeClasses(
        (isDisabledAndChecked || isErrorAdOptions) && '!cursor-default',
        publishIsFailed ? 'border-notif-red' : 'border-surface-strokes',
        'border rounded-xl p-4'
      )}
    >
      <Flex dir="row" items="center" justify="between" gap="gap-6" wFull>
        <Flex dir="col" gap="gap-6" items="start" justify="center" wFull>
          <Checkbox
            isDisabled={isDisabledAndChecked || isErrorAdOptions}
            inputClassName={composeClasses(
              isDisabledAndChecked && '!border-transparent',
              isErrorAdOptions && '!border-surface-gray-20'
            )}
            isChecked={isDisabledAndChecked ? isDisabledAndChecked : isChecked}
            onChange={() => ({})}
            type="checkbox"
          />
          <img alt="logo" src={img} width={214} height={24} />
        </Flex>
        <Flex wFull justify="end" items="center">
          {adOptions?.length === 0 && description && (
            <Text className="text-body-m text-primary max-w-[210px]">
              {description}
            </Text>
          )}
          {isLoadingAdOptions && adOptions?.length === 0 && (
            <Select
              onClose={() => ({})}
              isOpen={false}
              setIsOpen={() => ({})}
              classNameTrigger="w-full min-w-[210px]"
              classNameContainer="gap-2 px-3"
              isResponsive={false}
              isDisabled={true}
              customTriggerButton={
                <Flex
                  dir="row"
                  wFull
                  justify="between"
                  items="center"
                  className="border hover:bg-surface-gray-10 border-surface-strokes rounded-lg bg-white py-1.5 px-4"
                >
                  <Flex dir="col" items="start" justify="start">
                    <Text className="text-body-s text-secondary">
                      Cargando...
                    </Text>
                  </Flex>
                  <GoogleIcon name="expand_more" />
                </Flex>
              }
            >
              <div />
            </Select>
          )}
          {adOptions &&
            adOptions?.length === 0 &&
            isErrorAdOptions &&
            !isLoadingAdOptions && (
              <Flex dir="col" items="center" justify="start" gap="gap-1">
                <p className="text-xs text-notif-red">
                  Ocurrio un error al cargar los anuncios del portal
                </p>
              </Flex>
            )}
          {adOptions && adOptions?.length > 0 && (
            <Flex dir="col" items="center" justify="start" gap="gap-1">
              <Select
                strategyPosition="fixed"
                onClose={() => setOpenSelect(false)}
                isOpen={openSelect}
                setIsOpen={setOpenSelect}
                className={composeClasses(
                  (isDisabledAndChecked || !isChecked) &&
                    'pointer-events-none opacity-50'
                )}
                classNameTrigger="w-full min-w-[210px]"
                classNameContainer="gap-2 px-3 max-w-[210px]"
                isResponsive={false}
                customTriggerButton={
                  <Flex
                    dir="row"
                    wFull
                    justify="between"
                    items="center"
                    className={composeClasses(
                      'border hover:bg-surface-gray-10 border-surface-strokes rounded-lg bg-white py-1.5 px-4',
                      isChecked &&
                        !adOptionSelected?.adOptionId &&
                        'border-red-500'
                    )}
                  >
                    <Flex dir="col" items="start" justify="start">
                      <Text className="text-body-s text-secondary">
                        {isDisabledAndChecked
                          ? 'Publicada en portal'
                          : 'Tipo de anuncio'}
                      </Text>
                      {adOptionSelected && (
                        <Text className="line-clamp-2 capitalize">
                          {adOptionSelected?.name ||
                            adOptionSelected?.adOptionId?.toLowerCase()}
                        </Text>
                      )}
                    </Flex>
                    <GoogleIcon
                      name={openSelect ? 'expand_less' : 'expand_more'}
                    />
                  </Flex>
                }
              >
                {adOptions?.map((option) => {
                  const optionIsSelected = Boolean(
                    selectedAdOptions?.find(
                      (selectedAdOption) =>
                        selectedAdOption?.portalId === id &&
                        selectedAdOption?.adOptionId === option?.id
                    )
                  )

                  return (
                    <button
                      type="button"
                      className={composeClasses(
                        optionIsSelected
                          ? 'font-bold pointer-events-none'
                          : 'hover:font-semibold',
                        'w-full text-body-m text-primary text-left capitalize'
                      )}
                      key={option?.id + option?.name}
                      onClick={(event) => {
                        event.stopPropagation()
                        toggleSelectAdOption(
                          id,
                          option?.id,
                          option?.name,
                          option?.available
                        )
                        setOpenSelect(false)
                      }}
                    >
                      {option?.name}
                    </button>
                  )
                })}
              </Select>
              {(adOptionSelected?.available ||
                adOptionSelected?.available === 0) && (
                <Flex
                  dir="row"
                  items="center"
                  justify="start"
                  gap="gap-1"
                  wFull
                  className="pl-4"
                >
                  <GoogleIcon
                    name={adOptionSelected?.available === 0 ? 'error' : 'info'}
                    className={composeClasses(
                      adOptionSelected?.available === 0
                        ? 'text-red-500'
                        : 'text-primary',
                      'text-[12px]'
                    )}
                  />
                  <Text
                    className={composeClasses(
                      adOptionSelected?.available === 0
                        ? 'text-red-500'
                        : 'text-primary',
                      'text-body-s'
                    )}
                  >
                    {adOptionSelected?.available}{' '}
                    {adOptionSelected?.available === 1
                      ? 'anuncio disponible'
                      : 'anuncios disponibles'}
                  </Text>
                </Flex>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
      {delayedText && (
        <Text className="leading-4 font-[467] text-[11px] flex flex-row gap-2 items-center justify-start text-secondary rounded-[4px] bg-surface-gray-10 w-full text-left p-2">
          {delayedText}
        </Text>
      )}
      {publishIsFailed && (
        <Text className="leading-4 font-[467] text-[11px] flex flex-row gap-2 items-center justify-start text-notif-red rounded-[4px]  w-full text-left p-2">
          Ocurrió un error, intenta publicar la propiedad de nuevo en este
          portal.
        </Text>
      )}
    </Flex>
  )
}
